import axios from "axios";
import RestClient from "../lib/RestClient";

interface CreateDraftOrderParams {
  scanningLocationId: number;
  price: number;
  bodyPartIds: number[];
  scanTypeId: string;
  searchId: string;
  slotIds?: number[];
  googleAppointmentsToken?: string;
}

// Thrown when the server rejects the booking with a 422 (most often the chosen slot has just been taken, but also
// any other validation failure). Callers catch this to show the server's message inline and let the patient try
// again, rather than treating it as an unexpected failure.
export class BookingRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BookingRejectedError";
  }
}

// Generic copy for any failure that is not a specific slot-availability rejection. Mirrors the backend's
// DraftOrdersController::BOOKING_ERROR_MESSAGE so the patient sees the same wording whether the failure is a
// server-side validation reject (422) or a transport error (network / 5xx).
export const BOOKING_ERROR_MESSAGE =
  "Sorry, we couldn't complete this booking. Please try again, or contact us if the problem continues.";

export const createDraftOrder = async (params: CreateDraftOrderParams) => {
  let response;

  try {
    response = await RestClient.post("/draft_orders", {
      order_params: {
        scanning_location_id: params.scanningLocationId,
        price: params.price,
        body_part_ids: params.bodyPartIds,
        modality_id: params.scanTypeId,
        search_id: params.searchId,
        order_availability_slots_attributes: params.slotIds?.map((id) => ({
          availability_slot_id: id,
        })),
        google_appointments_token: params.googleAppointmentsToken,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      const message =
        (error.response.data as { error?: string })?.error ??
        BOOKING_ERROR_MESSAGE;
      throw new BookingRejectedError(message);
    }

    throw error;
  }

  if (typeof response.data.redirect_url === "string") {
    window.location.href = response.data.redirect_url;

    return;
  }

  throw new Error(
    `Expected a redirect URL in response, got ${JSON.stringify(response.data)}`
  );
};
